import React from 'react';
import { alpha, Button, ButtonProps } from '@mui/material';

/**
 * Brand pill button — the app's primary action style. Rounded, branded fill,
 * restated for the dark theme's glass button root (which otherwise forces navy).
 *
 * Use this instead of a bare MUI <Button> for primary actions so new buttons are
 * on-style by default rather than starting as default MUI and being restyled ad
 * hoc each time. Accepts all Button props; a caller `sx` is merged on top.
 *
 * `href` renders an anchor, exactly as MUI's Button does. The anchor attributes
 * come with it because a link that opens a new tab needs `target` and `rel`
 * together — plain ButtonProps drops both, which left callers reaching for a bare
 * Button and losing the pill styling.
 */
type PillButtonProps = ButtonProps & Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel' | 'download'>;

export const PillButton = ({ sx, variant = 'contained', size = 'small', color, ...props }: PillButtonProps) => (
	<Button
		variant={variant}
		size={size}
		color={color}
		{...props}
		sx={[
			(theme) => ({
				borderRadius: '999px',
				px: 2,
				py: 0.5,
				minWidth: 0,
				fontSize: '0.8125rem',
				fontWeight: 600,
				textTransform: 'none',
				// The branded fill belongs to the primary (contained) treatment only.
				// Applying it for every variant meant `variant="outlined"` silently
				// still rendered a filled button, so a caller had no way to express a
				// secondary action without overriding the atom at the call site.
				...(variant === 'contained' && {
					'&:not(.Mui-disabled)': {
						color: theme.palette.primary.contrastText,
						backgroundColor: theme.palette.primary.main,
						border: `1px solid ${alpha(theme.palette.primary.main, 0.6)}`,
						boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
						'&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.85) },
						// The themes force every un-coloured SvgIcon to a fixed value —
						// primary.main in light, #fff in dark — so on this button's navy
						// fill a light-theme icon came out navy-on-navy while dark looked
						// right. Icons follow the label instead, which is correct in both.
						// The other two variants set their own icon colour below.
						'& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
							color: 'inherit'
						}
					}
				}),
				// Destructive: the one action that must not read as neutral. Outlined
				// rather than filled, so "delete everything" is unmistakable without
				// out-shouting the primary action beside it. Without this a caller had
				// no way to express a destructive action through the atom at all, which
				// is why such buttons reached for a bare MUI Button and lost the pill.
				...(color === 'error' &&
					variant !== 'contained' && {
						'&:not(.Mui-disabled)': {
							color: theme.palette.error.main,
							backgroundColor: 'transparent',
							...(variant === 'outlined' && {
								border: '1px solid',
								borderColor: alpha(theme.palette.error.main, 0.5)
							}),
							boxShadow: 'none',
							backdropFilter: 'none',
							'& .MuiSvgIcon-root': { color: theme.palette.error.main },
							'&:hover': {
								backgroundColor: alpha(theme.palette.error.main, 0.08),
								borderColor: theme.palette.error.main,
								boxShadow: 'none',
								transform: 'none'
							}
						}
					}),
				// Secondary: the app's flat, non-glass secondary button — neutral, not
				// primary-coloured. Mirrors SECONDARY_BUTTON_SX in dataGridToolbar and
				// the same treatment hand-rolled in Admin/System, KnowledgeBase and
				// Profile/Edit. A primary-tinted border and text here just reproduces
				// stock MUI outlined-primary, which is not the app's language.
				...(variant === 'outlined' &&
					color !== 'error' && {
						'&:not(.Mui-disabled)': {
							color: theme.palette.text.secondary,
							backgroundColor: 'transparent',
							border: '1px solid',
							borderColor: theme.palette.divider,
							boxShadow: 'none',
							backdropFilter: 'none',
							'& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
							'&:hover': {
								backgroundColor: theme.palette.action.hover,
								borderColor: theme.palette.text.secondary,
								boxShadow: 'none',
								transform: 'none'
							}
						}
					}),
				// Tertiary: the quietest of the three — same neutral colour, no border.
				...(variant === 'text' && {
					'&:not(.Mui-disabled)': {
						color: theme.palette.text.secondary,
						boxShadow: 'none',
						'& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
						'&:hover': { backgroundColor: theme.palette.action.hover, boxShadow: 'none' }
					}
				})
			}),
			...(Array.isArray(sx) ? sx : [sx])
		]}
	/>
);

export default PillButton;
